import { FormEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';

import { DashValidation } from 'dash-common';
import {
  Form,
  FormErrorTypes,
  Modal,
  useFormState,
  useFormValidation,
} from 'dash-ui';

import useFormDraftFileUpload from 'common/hooks/useDraftFileUpload';
import {
  GetUnitsDocument,
  useAddFloorPlanToUnitMutation,
} from 'gql/generated.graphql';

enum FormFields {
  FloorPlanFileId = 'floorPlanFileId',
}

const AddFloorPlanModal = ({
  unitId,
  onClose,
}: {
  unitId: string | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['common', 'forms', 'pages']);
  const [formState, [setFloorPlan], [formOnSubmit, , resetFormState]] =
    useFormState([FormFields.FloorPlanFileId]);
  const [formErrors, , [setFloorPlanFileIdError], [resetFormErrors]] =
    useFormValidation([FormFields.FloorPlanFileId]);
  const formRef = useRef<HTMLFormElement>(null);

  const closeModal = () => {
    onClose();
    resetFormState();
    resetFormErrors();
  };

  const [addFloorPlanToUnit, { loading }] = useAddFloorPlanToUnitMutation({
    refetchQueries: [GetUnitsDocument],
    onCompleted: closeModal,
  });

  const draftFileUpload = useFormDraftFileUpload();

  useEffect(() => {
    setFloorPlan(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { floorPlanFileId } = formState;

    if (!floorPlanFileId) {
      return setFloorPlanFileIdError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.fileBlank'),
      });
    }

    return addFloorPlanToUnit({ variables: { floorPlanFileId, unitId } });
  };

  return (
    <Modal
      title={t('pages:propertyPage.addFloorPlan') || ''}
      isOpen={!!unitId}
      onRequestClose={closeModal}
      submitButton={{
        isLoading: loading,
        title: t('common:labels.add'),
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{ title: t('common:labels.cancel'), onClick: closeModal }}
    >
      <Form ref={formRef} onSubmit={onSubmit} mt={3}>
        <Form.Group>
          <Form.Label>{t('pages:propertyPage.floorPlan')}</Form.Label>
          <Form.FileUpload.Single
            accept={{ 'application/pdf': ['.pdf'] }}
            onUpload={draftFileUpload}
            onChanged={(fileId) => {
              setFloorPlan(fileId);
              setFloorPlanFileIdError(null);
            }}
            onDropRejected={(error) => {
              setFloorPlanFileIdError({
                type: FormErrorTypes.Invalid,
                message: t(
                  `forms:messages.uploadErrors.${camelCase(error.code)}`,
                  {
                    type: '.pdf',
                    maxSize: DashValidation.MaxLogoFileSize / 1024 / 1024,
                  }
                ),
              });
            }}
            maxSize={DashValidation.MaxLogoFileSize}
            $errorType={formErrors[FormFields.FloorPlanFileId]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[FormFields.FloorPlanFileId]?.type}
          >
            {formErrors[FormFields.FloorPlanFileId]?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default AddFloorPlanModal;
